import { useEffect, useState } from 'react';
import { formatDate } from '../../../lib/format';
import { useNavigate } from 'react-router-dom';
import type {
  PlatformUserRow, UserStatus,
} from '@dru/types';
import { USER_STATUS_LABEL } from '@dru/types';
import { Plus, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  Alert, Button, Checkbox, DataTable, Dialog, ErrorBanner, FilterBar, SearchInput, SearchSelect,
  StatusBadge, TextField, useToast,
} from '@dru/ui';
import type { Column } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { useAuth, errorMessage } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { NotForYourRole } from './Audit';
import { roleHint } from '../components/SecurityRoleHints';

/**
 * The platform's own operators.
 *
 * Deliberately not "every user". A company's users belong to that company and
 * are managed from its own console, under a role that cannot be granted by
 * somebody who does not hold its permissions. There is no
 * `crosstenant.user.read` anywhere in the catalogue, and adding one so this
 * screen could list them would turn a staff directory into a roster of who
 * works for which customer.
 *
 * The column that earns its place is the second factor. Everybody here holds
 * acts that a stolen password must not be enough for, and once
 * `MFA_ENFORCEMENT` is on, an operator without one simply cannot work. Seeing
 * that before the switch is flipped is the difference between a planned
 * rollout and an outage.
 */
export function Users() {
  const { permissions } = useAuth();
  if (!(permissions.has('user.read'))) {
    return <NotForYourRole title="Platform users" what="The list of platform staff is managed by platform administrators." />;
  }
  return <PlatformUsers />;
}

function PlatformUsers() {
  const nav = useNavigate();
  const { permissions: held } = useAuth();
  // Adding an operator needs both: the create itself, and the list of roles
  // the new account can be given (`user.role-assign`). Offering the button
  // without the second opened a form whose only required choice 403'd.
  const canCreate = held.has('user.create') && held.has('user.role-assign');
  const toast = useToast();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Searched on the server; one request per pause in typing, not per key.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [creating, setCreating] = useState(false);

  const users = usePages<PlatformUserRow>(
    (cursor) => api.get<{ items: PlatformUserRow[]; nextCursor: string | null }>(`/admin/platform-users?limit=100${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }${filters.status ? `&status=${filters.status}` : ''}${cursor ? `&cursor=${cursor}` : ''}`),
    [search, filters.status],
  );

  // The security picture, only once every operator is loaded: a count off
  // the first page would understate the gap it exists to show.
  const everyone = !users.hasMore && !users.loading && !search && !filters.status;
  const active = users.items.filter((u) => u.status === 'ACTIVE');
  const noMfa = active.filter((u) => !u.mfaEnabled).length;
  const invited = users.items.filter((u) => u.status === 'INVITED').length;
  const noRole = active.filter((u) => u.roles.length === 0).length;

  const rows = (users.items).filter((u) => {
    if (filters.mfa === 'missing') return !u.mfaEnabled;
    if (filters.mfa === 'active') return u.mfaEnabled;
    return true;
  });


  const columns: Column<PlatformUserRow>[] = [
    {
      key: 'person',
      header: 'Person',
      alwaysVisible: true,
      cell: (u) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">{u.fullName}</div>
          <div className="truncate text-caption font-normal text-neutral-500">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      cell: (u) => (
        <div className="flex flex-wrap gap-1">
          {u.roles.length === 0
            ? <span className="text-caption font-medium text-warning-600">No role — can do nothing</span>
            : u.roles.map((r) => (
                <span key={r.key} className="rounded-badge bg-neutral-100 px-1.5 py-0.5 text-caption text-neutral-700">
                  {r.name || r.key}
                </span>
              ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (u) => (
        <StatusBadge tone={STATUS_TONE[u.status] ?? 'neutral'}>
          {USER_STATUS_LABEL[u.status] ?? u.status}
        </StatusBadge>
      ),
    },
    {
      key: 'mfa',
      header: 'Two-factor',
      cell: (u) => (u.mfaEnabled ? (
        <span className="inline-flex items-center gap-1 text-small text-success-600">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> On
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-small text-warning-600">
          <ShieldOff className="h-3.5 w-3.5" aria-hidden /> Not set up
        </span>
      )),
    },
    {
      key: 'lastLogin',
      header: 'Last signed in',
      align: 'right',
      cell: (u) => (
        <div className="text-caption">
          <div className="text-neutral-700">{u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}</div>
          <div className="text-neutral-500">
            {u.liveSessions ? `${u.liveSessions} live session${u.liveSessions === 1 ? '' : 's'}` : 'Not signed in now'}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Platform users"
        hint="The platform’s own staff. A company’s users are managed from that company’s console — there is deliberately no way to list them from here."
        actions={canCreate && (
          <Button onClick={() => setCreating(true)} icon={<Plus className="h-4 w-4" aria-hidden />}>
            Add operator
          </Button>
        )}
      />


      {everyone && (noMfa > 0 || noRole > 0 || invited > 0) && (
        <Alert tone={noMfa || noRole ? 'warning' : 'info'} title="Account security">
          <ul className="mt-1 space-y-1">
            {noMfa > 0 && (
              <li>
                {noMfa === active.length ? `None of the ${active.length} active operators has` : `${noMfa} of ${active.length} active operators have not`}{' '}
                {noMfa === active.length ? 'two-factor sign-in set up' : 'set up two-factor sign-in'}. Each must enrol from their own Security page; nobody can do it for them.{' '}
                {filters.mfa !== 'missing' && (
                  <button type="button" className="font-medium text-primary-700 underline-offset-2 hover:underline"
                    onClick={() => setFilters((f) => ({ ...f, mfa: 'missing' }))}>Show them</button>
                )}
              </li>
            )}
            {noRole > 0 && <li>{noRole} active {noRole === 1 ? 'operator has' : 'operators have'} no role and can do nothing after signing in.</li>}
            {invited > 0 && <li>{invited} {invited === 1 ? 'invitation has' : 'invitations have'} not been accepted yet. Resend from the person’s page if the link expired.</li>}
          </ul>
        </Alert>
      )}

      <FilterBar
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: (Object.keys(USER_STATUS_LABEL) as UserStatus[])
              .map((s) => ({ value: s, label: USER_STATUS_LABEL[s] })),
          },
          {
            key: 'mfa',
            label: 'Two-factor',
            options: [
              { value: 'missing', label: 'Not set up' },
              { value: 'active', label: 'On' },
            ],
          },
        ]}
        value={filters}
        onChange={setFilters}
      >
        <SearchInput
          value={searchInput}
          onValueChange={setSearchInput}
          placeholder="Search name or email"
          aria-label="Search operators by name or email"
          className="w-full sm:w-72"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        loading={users.loading}
        error={users.error}
        onRetry={users.reload}
        onLoadMore={users.loadMore}
        hasMore={users.hasMore}
        loadingMore={users.loadingMore}
        onRowClick={(u) => nav(`/admin/users/${u.id}`)}
        columnsKey="admin.platform-users"
        total={users.hasMore ? undefined : users.items.length}
        empty={search || filters.status || filters.mfa
          ? { title: 'No operator matches', hint: 'Try another name, or clear the filters to see everyone.' }
          : { title: 'No operators yet', hint: canCreate ? 'Add the first operator; they choose their own password from the invitation.' : undefined }}
      />

      {creating && (
        <CreateOperator
          onClose={() => setCreating(false)}
          onCreated={(name, sent) => {
            toast.toast('success', sent
              ? `${name} invited. They choose their own password from the emailed link.`
              : `${name} created, but the invitation email did not go out. Resend it from their page.`);
            setCreating(false);
            users.reload();
          }}
        />
      )}
    </div>
  );
}

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  SUSPENDED: 'danger',
  DEACTIVATED: 'neutral',
};

function CreateOperator({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (name: string, sent: boolean) => void;
}) {
  const roles = useQuery<{ items: Array<{ key: string; name: string; description?: string | null; permissionCount: number; listed: boolean }> }>(
    () => api.get('/admin/platform-users/roles'), [],
  );
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleKey, setRoleKey] = useState('');
  // The MVP roles by default; the older ones stay assignable behind a toggle.
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<{ id: string }>('/admin/platform-users', { email, fullName, roleKey });
      let sent = true;
      try { await api.post(`/admin/platform-users/${created.id}/invite`); } catch { sent = false; }
      onCreated(fullName, sent);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const listed = (roles.data?.items ?? []).filter((r) => showAll || r.listed);
  const chosen = listed.find((r) => r.key === roleKey);
  const hint = chosen ? roleHint(chosen) : null;

  return (
    <Dialog
      title="Add a platform operator"
      info="The account is created invited, with no password, and an email asks them to choose one. Nobody here ever sees it — creating an operator never puts a credential in your hands."
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy} disabled={!email.includes('@') || fullName.trim().length < 2 || !roleKey}>
            Send invitation
          </Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">

        <TextField label="Full name" required value={fullName} onChange={setFullName} autoFocus />
        <TextField label="Work email" required type="email" value={email} onChange={setEmail} />

        <SearchSelect
          label="Role"
          required
          value={roleKey}
          onChange={setRoleKey}
          disabled={roles.loading}
          hint="Only roles you could grant yourself are listed."
          info="A platform account holds one platform-wide role."
          placeholder={roles.loading ? 'Loading roles…' : 'Choose a role…'}
          searchPlaceholder="Search roles…"
          options={listed.map((r) => ({ value: r.key, label: r.name }))}
        />
        {chosen && (
          <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small text-neutral-700">
            <span className="font-medium text-neutral-900">{chosen.name}</span>
            <span className="text-neutral-500"> · {chosen.permissionCount} permissions</span>
            {hint && <p className="mt-0.5">{hint}</p>}
          </div>
        )}
        <Checkbox
          label={<span className="text-caption text-neutral-700">Include older roles kept for existing accounts</span>}
          checked={showAll}
          onChange={(e) => { setShowAll(e.target.checked); setRoleKey(''); }}
        />

        {roles.error && <ErrorBanner message={roles.error} />}
        {error && <ErrorBanner message={error} />}
      </div>
    </Dialog>
  );
}
